import { supabase } from '@/lib/supabase-singleton';

// Simple in-memory cache for search results (clears after 30 seconds)
const searchCache = new Map<string, { results: UserSearchResult[], timestamp: number }>();
const CACHE_TTL = 30000; // 30 seconds

export interface UserSearchResult {
  id: string;
  username: string;
  display_name?: string | null;
  avatar?: string | null;  // Column is 'avatar' not 'avatar_url' in users table
  public_id?: string | null;
  // Optional status fields retained for UI compatibility; not populated by search
  is_online?: boolean;
  is_friend?: boolean;
  friend_request_status?: 'none' | 'sent' | 'received' | 'pending';
}

/**
 * Search for users by username, public_id (#XXXX), or full handle (username#XXXX)
 * @param input - Search query
 * @param limit - Maximum results to return (default 20)
 * @returns Array of matching users
 */
export async function searchUsers(input: string, limit = 20): Promise<UserSearchResult[]> {
  const q = (input ?? '').trim();
  if (!q) return [];

  // Exclude current user
  const { data: session } = await supabase.auth.getSession();
  const me = session?.session?.user?.id || '';

  const { data, error } = await supabase
    .from('users')
    .select('id, username, display_name, avatar, public_id')
    .or(`username.ilike.${q}%,display_name.ilike.${q}%`)
    .neq('supabase_auth_id', me)
    .order('username', { ascending: true })
    .limit(Math.max(1, Math.min(50, limit)));

  if (error) {
    console.error('[Search] Error:', error);
    throw error;
  }
  return (data || []).map((u) => ({
    id: (u as any).id,
    username: (u as any).username,
    display_name: (u as any).display_name,
    avatar: (u as any).avatar,
    public_id: (u as any).public_id,
    friend_request_status: 'none',
  }));
}

/**
 * Enhanced search that includes friend request status and full user details
 * ULTRA-OPTIMIZED VERSION: Prefix-based progressive search
 * @param input - Search query  
 * @param limit - Maximum results to return (default 10)
 * @returns Array of matching users with friend status
 */
export async function searchUsersForFriends(input: string, limit = 10): Promise<UserSearchResult[]> {
  const term = (input ?? '').trim();
  if (!term) return [];

  const cacheKey = `${term.toLowerCase()}-${limit}`;
  const cached = searchCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.results;
  }

  const { data: session } = await supabase.auth.getSession();
  const me = session?.session?.user?.id || '';

  const { data, error } = await supabase
    .from('users')
    .select('id, username, display_name, avatar, public_id')
    .or(`username.ilike.${term}%,display_name.ilike.${term}%`)
    .neq('supabase_auth_id', me)
    .order('username', { ascending: true })
    .limit(Math.max(1, Math.min(50, limit)));

  if (error) {
    console.error('[FriendSearch] Error:', error);
    return [];
  }

  const results: UserSearchResult[] = (data || []).map((u: any) => ({
    id: u.id,
    username: u.username,
    display_name: u.display_name,
    avatar: u.avatar,
    public_id: u.public_id,
    friend_request_status: 'none',
  }));

  searchCache.set(cacheKey, { results, timestamp: Date.now() });
  return results;
}

/**
 * Get a single user by their ID
 * @param userId - User ID
 * @returns User data or null
 */
export async function getUserById(userId: string): Promise<UserSearchResult | null> {
  const { data, error } = await supabase
    .from('users')
    .select('id, username, display_name, avatar, public_id')
    .eq('id', userId)
    .single();

  if (error) {
    console.error('[Search] Error fetching user:', error);
    return null;
  }
  return data as unknown as UserSearchResult;
}
